import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Calendar, Clock, MapPin, User, Phone, Mail, Package, XCircle, Home, AlertCircle, Star } from 'lucide-react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { db } from '../config/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import PhonePeService from '../utils/phonepeService';
import { sendOrderEmails } from '../utils/emailService';
import { ReviewForm } from '../components/ReviewSystem';

export default function OrderConfirmation() {
  const { user } = useAuth();
  const cart = useCart();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);

  useEffect(() => {
    const loadOrder = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get order ID from various sources
        const orderIdFromQuery = searchParams.get('orderId');
        const txnId = searchParams.get('txnId');
        const pendingOrderId = localStorage.getItem('pendingOrderId'); // For PhonePe
        
        // Check if this is a cancellation payment
        const pendingCancellationStr = localStorage.getItem('pendingCancellation');
        const pendingCancellation = pendingCancellationStr ? JSON.parse(pendingCancellationStr) : null;
        
        const orderIdToFetch = orderIdFromQuery || id || pendingCancellation?.orderId || pendingOrderId;

        console.log('=== Order Confirmation Loading ===');
        console.log('Order ID from query:', orderIdFromQuery);
        console.log('Order ID from params:', id);
        console.log('Pending Order ID (localStorage):', pendingOrderId);
        console.log('Pending Cancellation:', pendingCancellation);
        console.log('Transaction ID:', txnId);
        console.log('Full URL:', window.location.href);
        console.log('Final Order ID to fetch:', orderIdToFetch);

        if (!orderIdToFetch) {
          console.error('No order ID provided from any source');
          setError('No order ID provided');
          setLoading(false);
          return;
        }

        // Add delay to ensure Firestore write has completed
        console.log('Waiting for Firestore write to complete...');
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Try to fetch order from Firestore with retries
        const orderRef = doc(db, 'orders', orderIdToFetch);
        let orderSnap = await getDoc(orderRef);
        let retryCount = 0;
        const maxRetries = 3;

        while (!orderSnap.exists() && retryCount < maxRetries) {
          console.log(`Order not found, retry ${retryCount + 1}/${maxRetries}...`);
          await new Promise(resolve => setTimeout(resolve, 2000));
          orderSnap = await getDoc(orderRef);
          retryCount++;
        }

        if (orderSnap.exists()) {
          const orderData: any = { id: orderSnap.id, ...orderSnap.data() };
          console.log('✅ Order found:', orderData);
          setOrderDetails(orderData);
          
          // Clear pending order ID from localStorage
          if (pendingOrderId) {
            localStorage.removeItem('pendingOrderId');
            localStorage.removeItem('pendingOrderData');
            console.log('Cleared pending order data from localStorage');
          }

          // Handle CANCELLATION PAYMENT for PhonePe
          if (pendingCancellation && txnId) {
            console.log('🔄 Processing cancellation payment verification...');
            
            await new Promise(resolve => setTimeout(resolve, 3000));
            
            const verification = await PhonePeService.verifyPayment(txnId);
            console.log('Cancellation Payment Verification:', verification);
            
            if (verification.success && verification.code === 'PAYMENT_SUCCESS') {
              console.log('✅ Cancellation fee paid successfully');
              setPaymentVerified(true);
              setPaymentFailed(false);
              
              // Now cancel the order after successful payment
              try {
                await updateDoc(orderRef, {
                  cancelledAt: new Date().toISOString(),
                  cancelReason: pendingCancellation.cancelReason,
                  cancelledBy: 'user',
                  cancellationFee: pendingCancellation.cancellationData.fee,
                  cancellationFeePaid: true,
                  cancellationFeeTransactionId: txnId,
                  refundAmount: pendingCancellation.cancellationData.refundAmount,
                  refundStatus: 'approved',
                  updatedAt: new Date().toISOString(),
                  items: orderData.items?.map((item: any) => ({
                    ...item,
                    status: 'cancelled',
                    cancelledAt: new Date().toISOString(),
                    cancelReason: pendingCancellation.cancelReason
                  }))
                });
                
                console.log('✅ Order cancelled after payment confirmation');
                localStorage.removeItem('pendingCancellation');
                
                // Reload order to get updated data
                const updatedSnap = await getDoc(orderRef);
                if (updatedSnap.exists()) {
                  setOrderDetails({ id: updatedSnap.id, ...updatedSnap.data() });
                }
              } catch (updateError) {
                console.error('Failed to cancel order after payment:', updateError);
                setError('Payment successful but cancellation failed. Please contact support.');
              }
            } else {
              console.log('❌ Cancellation payment failed');
              setPaymentVerified(false);
              setPaymentFailed(true);
              
              localStorage.removeItem('pendingCancellation');
              setError('Cancellation payment failed. Order is NOT cancelled.');
            }
            
            // Clear cart cleared flag (no need for cancellation)
            return; // Exit early for cancellation flow
          }

          // Handle payment verification for PhonePe (REGULAR ORDER)
          if (orderData.payment?.method === 'phonepe' && txnId) {
            console.log('Verifying PhonePe payment with 3-second delay...');
            
            // Add 3-second delay before verification
            await new Promise(resolve => setTimeout(resolve, 3000));
            
            const verification = await PhonePeService.verifyPayment(txnId);
            
            console.log('PhonePe Verification Result:', verification);
            
            if (verification.success && verification.code === 'PAYMENT_SUCCESS') {
              console.log('✅ Payment verified successfully');
              setPaymentVerified(true);
              setPaymentFailed(false);
              
              // Update order status to 'placed' in Firestore
              try {
                await updateDoc(orderRef, {
                  status: 'placed',
                  'payment.status': 'completed',
                  updatedAt: new Date().toISOString()
                });
                console.log('Order status updated to placed');
              } catch (updateError) {
                console.error('Failed to update order status:', updateError);
              }
              
              // Clear cart only once
              const cartClearedKey = `cart_cleared_${orderIdToFetch}`;
              if (!localStorage.getItem(cartClearedKey)) {
                cart.clearCart();
                localStorage.setItem(cartClearedKey, 'true');
              }
            } else if (verification.code === 'PAYMENT_DECLINED' || 
                       verification.code === 'PAYMENT_CANCELLED' || 
                       verification.code === 'PAYMENT_ERROR' ||
                       verification.code === 'BAD_REQUEST' ||
                       verification.message?.toLowerCase().includes('cancel') ||
                       verification.message?.toLowerCase().includes('decline') ||
                       verification.message?.toLowerCase().includes('fail')) {
              console.log('❌ Payment cancelled or failed');
              setPaymentVerified(false);
              setPaymentFailed(true);
              
              // Determine failure reason
              const failureReason = 
                verification.code === 'PAYMENT_CANCELLED' || verification.message?.toLowerCase().includes('cancel')
                  ? 'Payment cancelled by user'
                  : verification.code === 'PAYMENT_DECLINED' || verification.message?.toLowerCase().includes('decline')
                  ? 'Payment declined by bank'
                  : verification.code === 'PAYMENT_ERROR' || verification.message?.toLowerCase().includes('fail')
                  ? 'Payment failed due to technical error'
                  : 'Payment failed';
              
              // Update order status to failed in Firestore
              try {
                // Update each item's payment status
                const updatedItems = orderData.items?.map((item: any) => ({
                  ...item,
                  payment: {
                    ...item.payment,
                    status: 'failed',
                    failureReason: failureReason,
                    failedAt: new Date().toISOString()
                  }
                }));
                
                await updateDoc(orderRef, {
                  items: updatedItems,
                  'payment.status': 'failed',
                  'payment.failureReason': failureReason,
                  'payment.failedAt': new Date().toISOString(),
                  'payment.transactionId': txnId,
                  failureDetails: {
                    reason: failureReason,
                    code: verification.code,
                    message: verification.message,
                    timestamp: new Date().toISOString()
                  },
                  updatedAt: new Date().toISOString()
                });
                console.log('Order payment status updated to failed:', failureReason);
              } catch (updateError) {
                console.error('Failed to update order status:', updateError);
              }
              
              // Don't clear cart - let user retry
            } else {
              console.log('⚠️ Payment verification pending');
              // Payment pending/processing
              setPaymentVerified(false);
              setPaymentFailed(false);
              // Still clear cart as order exists
              const cartClearedKey = `cart_cleared_${orderIdToFetch}`;
              if (!localStorage.getItem(cartClearedKey)) {
                cart.clearCart();
                localStorage.setItem(cartClearedKey, 'true');
              }
            }
          } else if (orderData.payment?.method === 'cod') {
            // COD orders are automatically confirmed
            setPaymentVerified(true);
            setPaymentFailed(false);
            const cartClearedKey = `cart_cleared_${orderIdToFetch}`;
            if (!localStorage.getItem(cartClearedKey)) {
              cart.clearCart();
              localStorage.setItem(cartClearedKey, 'true');
            }
          } else {
            // Other payment methods
            setPaymentVerified(true);
            setPaymentFailed(false);
          }

          setOrderDetails(orderData);
        } else {
          console.error('❌ Order not found after all retries');
          setError('Order not found. Please check your email for order confirmation or contact support.');
        }
      } catch (err) {
        console.error('❌ Error loading order:', err);
        setError('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [id, searchParams]);

  // Send emails after order is loaded and payment is verified
  useEffect(() => {
    const sendEmails = async () => {
      if (orderDetails && !emailSent && (paymentVerified || orderDetails.payment?.method === 'cod')) {
        console.log('Sending order confirmation emails...');
        try {
          // order documents sometimes store customer details under different keys
          const customer = orderDetails.customerInfo || orderDetails.address || {
            fullName: orderDetails.customerName || '',
            email: orderDetails.customerEmail || '',
            phone: orderDetails.customerPhone || ''
          };

          if (!customer || (!customer.fullName && !customer.email)) {
            console.warn('OrderConfirmation: customer info missing on orderDetails', orderDetails);
          }

          const emailData = {
            orderId: orderDetails.id,
            customerName: customer.fullName || (orderDetails.userId ? `User:${orderDetails.userId}` : ''),
            customerEmail: customer.email || '',
            customerPhone: customer.phone || '',
            items: orderDetails.items,
            address: orderDetails.address || customer,
            scheduling: orderDetails.scheduling,
            payment: orderDetails.payment
          };

          await sendOrderEmails(emailData);
          setEmailSent(true);
          console.log('✅ Order confirmation emails sent successfully');
        } catch (emailError) {
          console.error('❌ Failed to send emails:', emailError);
          // Don't fail the order if email fails
        }
      }
    };

    sendEmails();
  }, [orderDetails, paymentVerified, emailSent]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 md:py-12 px-3 md:px-4">
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 text-center max-w-md w-full">
          <div className="animate-spin rounded-full h-12 w-12 md:h-16 md:w-16 border-b-4 border-primary-600 mx-auto"></div>
          <p className="mt-4 md:mt-6 text-gray-600 font-medium text-sm md:text-base">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !orderDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 md:py-12 px-3 md:px-4">
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-12 text-center max-w-md w-full">
          <XCircle className="h-16 w-16 md:h-20 md:w-20 text-red-500 mx-auto mb-4 md:mb-6" />
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">Order Not Found</h2>
          <p className="text-gray-600 mb-6 md:mb-8 text-sm md:text-base">{error || 'We couldn\'t find your order'}</p>
          <Link 
            to="/" 
            className="inline-flex items-center px-5 py-2.5 md:px-6 md:py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm md:text-base"
          >
            <Home className="w-4 h-4 md:w-5 md:h-5 mr-2" />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 md:py-8 px-3 md:px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl mx-auto"
      >
        {/* Success Header */}
        <div className={`${
          paymentFailed
            ? 'bg-gradient-to-r from-red-500 to-red-600'
            : paymentVerified && orderDetails.payment?.method === 'phonepe' 
            ? 'bg-gradient-to-r from-green-500 to-green-600' 
            : orderDetails.payment?.method === 'phonepe' && !paymentVerified
            ? 'bg-gradient-to-r from-yellow-500 to-yellow-600'
            : 'bg-gradient-to-r from-green-500 to-green-600'
        } text-white rounded-lg shadow-lg p-4 md:p-6 lg:p-8 mb-4 md:mb-6 text-center`}>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
          >
            {paymentFailed ? (
              <XCircle className="h-14 w-14 md:h-16 md:w-16 lg:h-20 lg:w-20 mx-auto mb-3 md:mb-4" />
            ) : paymentVerified || orderDetails.payment?.method === 'cod' ? (
              <CheckCircle className="h-14 w-14 md:h-16 md:w-16 lg:h-20 lg:w-20 mx-auto mb-3 md:mb-4" />
            ) : (
              <Clock className="h-14 w-14 md:h-16 md:w-16 lg:h-20 lg:w-20 mx-auto mb-3 md:mb-4" />
            )}
          </motion.div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">
            {paymentFailed
              ? 'Payment Failed'
              : (paymentVerified && orderDetails.payment?.method === 'phonepe') || orderDetails.payment?.method === 'cod'
              ? 'Order Confirmed!' 
              : 'Payment Pending'}
          </h1>
          <p className={`${
            paymentFailed
              ? 'text-red-50'
              : (paymentVerified && orderDetails.payment?.method === 'phonepe') || orderDetails.payment?.method === 'cod'
              ? 'text-green-50'
              : 'text-yellow-50'
          } text-base md:text-lg`}>
            {paymentFailed
              ? 'Your payment was cancelled or declined'
              : (paymentVerified && orderDetails.payment?.method === 'phonepe') || orderDetails.payment?.method === 'cod'
              ? 'Your booking has been successfully placed'
              : 'Payment verification in progress'}
          </p>
          {paymentFailed ? (
            <p className="text-xs md:text-sm text-red-100 mt-2">
              This order has been cancelled. Please place a new order or try a different payment method.
            </p>
          ) : !paymentVerified && orderDetails.payment?.method === 'phonepe' && (
            <p className="text-xs md:text-sm text-yellow-100 mt-2">
              Your order is received. Payment status will be updated shortly.
            </p>
          )}
          <div className="mt-3 md:mt-4 inline-block bg-white/20 backdrop-blur-sm px-4 py-1.5 md:px-6 md:py-2 rounded-full">
            <p className="text-xs md:text-sm font-medium">Order ID: #{orderDetails.id.slice(-8)}</p>
          </div>
        </div>

        {/* Order Details Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-4 md:mb-6">
          {/* Services Section */}
          <div className="p-4 md:p-6 border-b border-gray-200">
            <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4 flex items-center">
              <Package className="w-5 h-5 md:w-6 md:h-6 mr-2 text-primary-600" />
              Ordered Services
            </h2>
            <div className="space-y-3 md:space-y-4">
              {orderDetails.items?.map((item: any, index: number) => (
                <div key={index} className="flex items-start space-x-3 md:space-x-4 p-3 md:p-4 bg-gray-50 rounded-lg">
                  <img
                    src={item.image || '/placeholder.jpg'}
                    alt={item.name}
                    className="w-16 h-16 md:w-20 md:h-20 rounded-lg object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm md:text-base truncate">{item.name}</h3>
                    <p className="text-xs md:text-sm text-gray-600">Quantity: {item.quantity}</p>
                    <p className="text-xs md:text-sm text-gray-600">₹{item.price}</p>
                    {item.addons && item.addons.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                        Add-ons: {item.addons.map((a: any) => a.name).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Scheduling Section */}
          {orderDetails.scheduling && (
            <div className="p-4 md:p-6 border-b border-gray-200">
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4 flex items-center">
                <Calendar className="w-5 h-5 md:w-6 md:h-6 mr-2 text-primary-600" />
                Scheduling
              </h2>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div className="flex items-center space-x-2 md:space-x-3">
                  <Calendar className="w-4 h-4 md:w-5 md:h-5 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs md:text-sm text-gray-600">Date</p>
                    <p className="font-medium text-gray-900 text-sm md:text-base truncate">{orderDetails.scheduling.date}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 md:space-x-3">
                  <Clock className="w-4 h-4 md:w-5 md:h-5 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs md:text-sm text-gray-600">Time</p>
                    <p className="font-medium text-gray-900 text-sm md:text-base truncate">{orderDetails.scheduling.time}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Address Section */}
          {orderDetails.address && (
            <div className="p-4 md:p-6 border-b border-gray-200">
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4 flex items-center">
                <MapPin className="w-5 h-5 md:w-6 md:h-6 mr-2 text-primary-600" />
                Service Address
              </h2>
              <div className="space-y-1 md:space-y-2 text-gray-700">
                <p className="font-medium text-sm md:text-base">{orderDetails.address.fullName}</p>
                <p className="text-sm md:text-base">{orderDetails.address.phone}</p>
                <p className="text-gray-600 text-xs md:text-sm leading-relaxed">
                  {[
                    orderDetails.address.houseNumber,
                    orderDetails.address.streetAddress,
                    orderDetails.address.landmark,
                    orderDetails.address.city,
                    orderDetails.address.state,
                    orderDetails.address.pincode
                  ].filter(Boolean).join(', ')}
                </p>
              </div>
            </div>
          )}

          {/* Payment Section */}
          {orderDetails.payment && (
            <div className="p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">Payment Summary</h2>
              <div className="space-y-2">
                <div className="flex justify-between text-gray-700">
                  <span>Subtotal</span>
                  <span>₹{orderDetails.payment.subtotal?.toLocaleString()}</span>
                </div>
                <div className="border-t pt-2 flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-primary-600">₹{orderDetails.payment.total?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600 pt-2">
                  <span>Payment Method</span>
                  <span className="font-medium uppercase">{orderDetails.payment.method}</span>
                </div>
                {orderDetails.payment?.method === 'phonepe' && (
                  <div className="flex justify-between text-sm text-gray-600 pt-2">
                    <span>Payment Status</span>
                    <span className={`font-medium ${
                      paymentFailed
                        ? 'text-red-600'
                        : paymentVerified 
                        ? 'text-green-600' 
                        : 'text-yellow-600'
                    }`}>
                      {paymentFailed 
                        ? '✗ Failed' 
                        : paymentVerified 
                        ? '✓ Verified' 
                        : '⏳ Pending Verification'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Next Steps */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-3">What's Next?</h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>You'll receive a confirmation call within 2 hours</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Our professional will arrive at the scheduled time</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Track your order in My Account section</span>
            </li>
          </ul>
        </div>

        {/* Review Section (for completed orders) */}
        {orderDetails.status === 'completed' && (
          <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-6">
            {!showReviewForm ? (
              <div className="text-center">
                <Star className="h-12 w-12 text-yellow-400 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  How was your experience?
                </h3>
                <p className="text-gray-600 mb-4">
                  Your feedback helps us improve our services
                </p>
                <button
                  onClick={() => setShowReviewForm(true)}
                  className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors font-medium"
                >
                  Write a Review
                </button>
              </div>
            ) : (
              <ReviewForm
                orderId={orderDetails.id}
                serviceId={orderDetails.items[0]?.id || ''}
                serviceName={orderDetails.items[0]?.name || 'Service'}
                onSuccess={() => {
                  setShowReviewForm(false);
                  // You can add a success toast here
                }}
                onCancel={() => setShowReviewForm(false)}
              />
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            to="/account?tab=orders"
            className="flex-1 bg-primary-600 text-white text-center py-3 px-6 rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            View My Orders
          </Link>
          <Link
            to="/services"
            className="flex-1 bg-white text-primary-600 border-2 border-primary-600 text-center py-3 px-6 rounded-lg hover:bg-primary-50 transition-colors font-medium"
          >
            Book More Services
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
